import React, { useContext, useState } from "react";
import { FlashContext, IntlContext, ListContext } from "context";
import { BookingInterface } from "hooks/booking";
import bookingValidationDataProvider, { BookingValidationStatus } from "hooks/bookingValidation";
import Button from "ui/Button";
import Modal from "ui/Modal";
import { Form } from "react-final-form";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Role } from "types/user";
import { Textarea } from "components/Form/Field";
import ConfirmModal from "ui/Modal/ConfirmModal";
import { FormApi } from "final-form";

interface RefuseServiceModalProps {
    isHiddenRefuseServiceModal: boolean;
    setIsHiddenRefuseServiceModal: (show: boolean) => void;
    booking: BookingInterface;
    dateFormatter: (date: string) => string;
    role: Role.CLIENT | Role.PROVIDER | undefined;
    startTimeSplit: string;
}

const RefuseServiceModal: React.FC<RefuseServiceModalProps> = ({
    isHiddenRefuseServiceModal,
    setIsHiddenRefuseServiceModal,
    booking,
    dateFormatter,
    role,
    startTimeSplit,
}) => {
    const intl = useContext(IntlContext);
    const listContext = useContext(ListContext);
    const flashMessage = useContext(FlashContext);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [data, setData] = useState<Record<string, unknown> | null>(null);

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    let initialValues = {
        booking_id: booking.id,
        status: BookingValidationStatus.NON_VALIDATED,
        duration: booking.nb_hours.toString().padStart(2, "0") + ":00",
    };

    if (!role) {
        return <></>;
    }

    const onSubmit = (data: Record<string, unknown>) => {
        setShowConfirmModal(true);
        setData(data);
        setIsHiddenRefuseServiceModal(true);
    };

    const handleSubmitForm = (form: FormApi) => {
        const { values } = form.getState();

        if (values.motive && values.motive.length >= 1) {
            setIsHiddenRefuseServiceModal(true);
            setShowConfirmModal(true);
            form.submit();
        }
    };

    const handleConfirmModal = async () => {
        if (data) {
            try {
                await bookingValidationDataProvider.create(data);
                listContext.refreshData();
                if(role === Role.CLIENT) flashMessage?.addSuccess(intl.formatMessage("booking.list.modal.refuse_services.success"));
                // TODO: Send notification
                setShowConfirmModal(false);
            } catch (err: unknown) {
                if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                    const responseData = err.response.data;
                    if (isRecord(responseData) && "data" in responseData) {
                        return formatErrorResponse(responseData as { data: unknown });
                    }
                }
                throw err;
            }
        }
    };

    return (
        <React.Fragment>
            <Modal
                onClose={() => setIsHiddenRefuseServiceModal(true)}
                isHidden={isHiddenRefuseServiceModal}
                title={intl.formatMessage("booking.list.modal.refuse_services.title", {
                    subcategory: booking.subcategory ? intl.formatMessage(`subcategory.${booking.category.toString().toLowerCase()}.${booking.subcategory.toString().toLowerCase()}`) : intl.formatMessage(`service.label.category.${booking.category.toString()}`),
                    to: role === Role.CLIENT ? "par" : "pour",
                    firstname: role === Role.CLIENT ? booking.provider.first_name : booking.client.first_name,
                    lastname: role === Role.CLIENT ? booking.provider.last_name : booking.client.last_name,
                    date: dateFormatter(booking.date),
                    start: parseInt(startTimeSplit),
                    end: parseInt(startTimeSplit) + booking.nb_hours,
                })}
                width={"medium"}
            >
                <Form
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    render={({ handleSubmit, form }) => {
                        return (
                            <form onSubmit={handleSubmit}>
                                <div className="flex flex--direction-column margin-bottom--5 flex--justify-content-center">
                                    <p>{role === Role.CLIENT ?
                                        intl.formatMessage("booking.list.modal.refuse_services.reason_of_contest") :
                                        intl.formatMessage("booking.list.modal.refuse_services.reason_of_refuse")
                                    }</p>

                                    <div className="margin-top--4 margin-bottom--4">
                                        <Textarea
                                            inputProps={{
                                                name: "motive",
                                            }}
                                            labelProps={{
                                                label: intl.formatMessage(
                                                    "booking.list.modal.refuse_services.input.reason"
                                                ) + " *",
                                            }}
                                        />
                                        <p>{role === Role.CLIENT ?
                                            intl.formatMessage("booking.list.modal.refuse_services.client_contest") :
                                            intl.formatMessage("booking.list.modal.refuse_services.provider_refuse")
                                        }</p>
                                    </div>

                                    <div className="text-center padding-top--4">
                                        <Button className="btn btn--cancel" onClick={() => handleSubmitForm(form)}>
                                            {role === Role.CLIENT ?
                                                intl.formatMessage("booking.list.links.label.contest.booking") :
                                                intl.formatMessage("booking.list.links.label.provider_cancel.booking")
                                            }
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        );
                    }}
                />
            </Modal>
            <ConfirmModal
                show={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false);
                }}
                onConfirm={handleConfirmModal}
                title={
                    role === Role.CLIENT
                        ? intl.formatMessage("booking.list.modal.refuse_services.second_validation.title")
                        : intl.formatMessage("booking.list.modal.refuse_services_provider.second_validation.title")
                }
                message={<span></span>}
                confirmButton={intl.formatMessage("authentication.reset_password.action.save")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
                cancelClassName="btn btn--cancel"
            />
        </React.Fragment>
    );
};

export default RefuseServiceModal;
